import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { MessageCircle, Send } from 'lucide-react';
import LinkedinIcon from "@/components/icons/LinkedinIcon";
import { ConversationItem, MessageItem } from '@/types/MessagingInterfaces';
import { MessageBubble } from './MessageBubble';
import { getCsrfToken, getInitials } from './messagingUtils';

interface ViewConversationDialogProps {
    conversationId: number | null;
    authUserId: number;
    speakerSlug: string;
    speakerName: string;
    autoOpen?: boolean;
}

export const ViewConversationDialog = ({
    conversationId: initialConversationId,
    authUserId,
    speakerSlug,
    speakerName,
    autoOpen = false}: ViewConversationDialogProps) => {
    const [open, setOpen] = useState(autoOpen);
    const [conversationId, setConversationId] = useState<number | null>(initialConversationId);
    const [conversation, setConversation] = useState<(ConversationItem & { messages: MessageItem[] }) | null>(null);
    const [loading, setLoading] = useState(false);
    const [body, setBody] = useState('');
    const [sending, setSending] = useState(false);
    const [bodyError, setBodyError] = useState<string | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const isNewConversation = conversationId === null;

    const loadConversation = async (id: number) => {
        setLoading(true);
        setLoadError(null);
        try {
            const response = await fetch(route('messages.data', id), {
                headers: { Accept: 'application/json' },
                credentials: 'same-origin'});
            if (!response.ok) throw new Error();
            const data = await response.json();
            setConversation(data);
        } catch {
            setLoadError('Could not load conversation. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open && conversationId !== null) {
            loadConversation(conversationId);
        }
    }, [open]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
    }, [conversation?.messages.length]);

    const otherUser = conversation
        ? conversation.user_id_1 === authUserId ? conversation.user2 : conversation.user1
        : null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!body.trim()) return;
        setSending(true);
        setBodyError(null);
        try {
            const csrfToken = getCsrfToken();

            if (isNewConversation) {
                const response = await fetch(route('messages.start', speakerSlug), {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                        'X-CSRF-TOKEN': csrfToken},
                    credentials: 'same-origin',
                    body: JSON.stringify({ body })});
                if (!response.ok) {
                    const data = await response.json();
                    setBodyError(data.errors?.body?.[0] ?? 'Failed to send message.');
                    return;
                }
                const newConversation: ConversationItem & { messages: MessageItem[] } = await response.json();
                setConversationId(newConversation.id);
                setConversation(newConversation);
            } else {
                const response = await fetch(route('messages.store', conversationId), {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                        'X-CSRF-TOKEN': csrfToken},
                    credentials: 'same-origin',
                    body: JSON.stringify({ body })});
                if (!response.ok) {
                    const data = await response.json();
                    setBodyError(data.errors?.body?.[0] ?? 'Failed to send message.');
                    return;
                }
                const newMessage: MessageItem = await response.json();
                setConversation((prev) =>
                    prev ? { ...prev, messages: [...prev.messages, newMessage] } : prev
                );
            }

            setBody('');
        } catch {
            setBodyError('Could not send message. Please try again.');
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button className="text-md px-3 sm:px-8">
                    <MessageCircle className="w-4 h-4" />
                    <span className="hidden sm:inline">{isNewConversation ? 'Contact Speaker' : 'View Conversation'}</span>
                    <span className="sm:hidden">{isNewConversation ? 'Contact' : 'View Chat'}</span>
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg flex flex-col max-h-[80vh] gap-0 p-0 overflow-hidden">
                <DialogHeader className="px-6 pt-6 pb-4 space-y-1.5 text-left">
                    {otherUser ? (
                        <div className="flex items-center gap-3">
                            <Avatar className="h-10 w-10 flex-shrink-0 ring-2 ring-background shadow-sm">
                                <AvatarImage src={otherUser.avatar_url ?? undefined} />
                                <AvatarFallback className="text-xs">{getInitials(otherUser)}</AvatarFallback>
                            </Avatar>
                            <div className="min-w-0 flex-1">
                                <DialogTitle className="text-base">{`${otherUser.firstname} ${otherUser.lastname}`}</DialogTitle>
                                {otherUser.linkedin_profile_url && (
                                    <a
                                        href={otherUser.linkedin_profile_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 text-xs text-[#0077B5] hover:underline mt-0.5"
                                    >
                                        <LinkedinIcon className="w-3 h-3" />
                                        LinkedIn Profile
                                    </a>
                                )}
                            </div>
                        </div>
                    ) : (
                        <DialogTitle>{`Conversation with ${speakerName}`}</DialogTitle>
                    )}
                    {isNewConversation && (
                        <DialogDescription>
                            Send a message to start a conversation. She will be able to reply from her profile.
                        </DialogDescription>
                    )}
                </DialogHeader>

                <div className="flex-1 overflow-y-auto space-y-4 px-6 pt-4 min-h-0 bg-muted/20">
                    {loading && (
                        <div className="space-y-3 px-1">
                            {[...Array(3)].map((_, i) => (
                                <div key={i} className={`flex gap-3 ${i % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                                    <div className="h-8 w-8 rounded-full bg-muted animate-pulse flex-shrink-0" />
                                    <div className="h-10 w-48 rounded-2xl bg-muted animate-pulse" />
                                </div>
                            ))}
                        </div>
                    )}
                    {loadError && (
                        <p className="text-left text-destructive text-sm py-8">{loadError}</p>
                    )}
                    {!loading && !loadError && !isNewConversation && conversation?.messages.length === 0 && (
                        <p className="text-left text-muted-foreground text-sm py-8">No messages yet.</p>
                    )}
                    {!loading && !loadError && conversation?.messages.map((message) => (
                        <MessageBubble key={message.id} message={message} authUserId={authUserId} />
                    ))}
                    <div ref={messagesEndRef} />
                </div>

                <div className="px-6 pb-4 flex-shrink-0 bg-background">
                    <form onSubmit={handleSubmit} className="space-y-2">
                        <Textarea
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            placeholder={isNewConversation
                                ? `Hi ${speakerName.split(' ')[0]}, I'd love to invite you to speak at…`
                                : 'Write a message…'}
                            rows={3}
                            className="resize-none"
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                                    handleSubmit(e as unknown as React.FormEvent);
                                }
                            }}
                        />
                        {bodyError && <p className="text-sm text-destructive">{bodyError}</p>}
                        <div className="flex flex-wrap items-center gap-3">
                            <Button type="submit" disabled={sending || !body.trim()}>
                                <Send className="h-4 w-4" />
                                {sending ? 'Sending…' : 'Send message'}
                            </Button>
                            <span className="text-xs text-muted-foreground">
                                {speakerName} will be notified via email.
                            </span>
                        </div>
                    </form>
                </div>
            </DialogContent>
        </Dialog>
    );
};
